import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Pagination,
  Select,
  type Column,
} from '@/components/ui';
import {
  useAuditAreas,
  useAuditEvents,
  type AuditEvent,
} from '@/features/audit/api';
import { PageContainer } from '@/layouts/PageContainer';
import { CLINIC_TZ } from '@/lib/clinic-time';
import styles from '@/pages/queue/queue.module.css';
import {
  EMPTY_FILTERS,
  filterErrors,
  toAuditQuery,
  type AuditFilterForm,
} from './audit-filters';
import gov from './governance.module.css';

const recordLabel = (type: string) => {
  const words = type.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Admin v2 "Audit log": every change in any role, with who, what and when.
 * Read-only. RL-1817: every area, plain labels, and date, person and record
 * filters (GET /audit-events, RL-1816).
 */
export function AuditLogPage() {
  const areas = useAuditAreas();
  const [draft, setDraft] = useState<AuditFilterForm>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilterForm>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useAuditEvents(
    toAuditQuery(applied, page),
  );
  const errors = filterErrors(draft);
  const invalid = Object.values(errors).some(Boolean);
  const set = <K extends keyof AuditFilterForm>(
    key: K,
    value: AuditFilterForm[K],
  ) => setDraft((d) => ({ ...d, [key]: value }));
  const apply = (next: AuditFilterForm) => {
    setDraft(next);
    setApplied(next);
    setPage(1);
  };
  /**
   * Area and Changes only apply at once, on top of the filters already
   * applied: half-typed fields stay drafts until Apply.
   */
  const applyNow = <K extends 'area' | 'changesOnly'>(
    key: K,
    value: AuditFilterForm[K],
  ) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setApplied((a) => ({ ...a, [key]: value }));
    setPage(1);
  };

  const columns: Column<AuditEvent>[] = [
    {
      key: 'when',
      header: 'When',
      render: (e) =>
        new Date(e.occurredAt).toLocaleString('en-GB', { timeZone: CLINIC_TZ }),
    },
    {
      key: 'who',
      header: 'Who',
      render: (e) =>
        e.actor?.fullName ??
        (e.actorType === 'system' ? 'System' : 'Anonymous'),
    },
    {
      key: 'what',
      header: 'What',
      render: (e) => (
        <>
          <p className={styles.name}>
            {e.label}
            {e.isView && (
              <>
                {' '}
                <Badge>Viewed</Badge>
              </>
            )}
          </p>
          <p className={styles.sub}>{e.areaLabel}</p>
        </>
      ),
    },
    { key: 'branch', header: 'Branch', render: (e) => e.branch?.name ?? '—' },
    {
      key: 'record',
      header: 'Record',
      render: (e) =>
        e.entityType ? (
          <>
            <span className={styles.sub}>
              {recordLabel(e.entityType)}{' '}
              {e.entityId && (
                <span className="mono">{e.entityId.slice(0, 8)}</span>
              )}
            </span>
            {e.entityId && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`History of ${recordLabel(e.entityType)} ${e.entityId.slice(0, 8)}`}
                onClick={() =>
                  apply({
                    ...applied,
                    recordType: e.entityType ?? '',
                    recordId: e.entityId ?? '',
                  })
                }
              >
                History
              </Button>
            )}
          </>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <PageContainer>
      <Card title="Audit log" flush>
        <form
          className={gov.filters}
          aria-label="Filter the audit log"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!invalid) apply(draft);
          }}
        >
          <Field label="Area">
            <Select
              value={draft.area}
              placeholder="All areas"
              options={(areas.data ?? []).map((a) => ({
                value: a.key,
                label: a.label,
              }))}
              onChange={(ev) => applyNow('area', ev.target.value)}
            />
          </Field>
          <Field label="From">
            <Input
              type="date"
              value={draft.fromDate}
              onChange={(ev) => set('fromDate', ev.target.value)}
            />
          </Field>
          <Field label="To" error={errors.toDate}>
            <Input
              type="date"
              value={draft.toDate}
              onChange={(ev) => set('toDate', ev.target.value)}
            />
          </Field>
          <Field label="Person" error={errors.person}>
            <Input
              placeholder="Part of a staff name"
              value={draft.person}
              onChange={(ev) => set('person', ev.target.value)}
            />
          </Field>
          <Field label="Record type" error={errors.recordType}>
            <Input
              placeholder="e.g. patient, bill"
              value={draft.recordType}
              onChange={(ev) => set('recordType', ev.target.value)}
            />
          </Field>
          <Field label="Record ID" error={errors.recordId}>
            <Input
              className="mono"
              value={draft.recordId}
              onChange={(ev) => set('recordId', ev.target.value)}
            />
          </Field>
          <div className={gov.filterActions}>
            <label className={gov.check}>
              <input
                type="checkbox"
                checked={draft.changesOnly}
                onChange={(ev) => applyNow('changesOnly', ev.target.checked)}
              />
              Changes only
            </label>
            <Button type="submit" variant="primary" disabled={invalid}>
              Apply
            </Button>
            <Button type="button" onClick={() => apply(EMPTY_FILTERS)}>
              Clear
            </Button>
          </div>
        </form>
        <DataTable
          caption="Audit log"
          columns={columns}
          rows={data?.data}
          rowKey={(e) => e.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={<EmptyState title="No events match these filters" />}
          footer={
            data?.meta && <Pagination meta={data.meta} onPageChange={setPage} />
          }
        />
      </Card>
    </PageContainer>
  );
}
